import { DateTime } from "luxon";

import type { Department } from "../contexts/AuthProvider";
import type { HospitalDataContextType } from "../contexts/HospitalDataProvider";
import {
	checkMoreThanAllowedConsecutiveShifts,
	getNurseIdsForShift,
	handleOverTimeNurse,
	isCannotWork,
	isNurseScheduledAdjacent,
	isWeekend,
	type ShiftDetails,
	type StaffingContext,
	type StaffingIssue,
	type Suggestion,
	sortNurses,
	updateNurseCount,
} from "./autoStaffing";
import { formatPersonFullName } from "./formatters";
import {
	type Day,
	EmploymentType,
	type HospitalData,
	IssuePriority,
	IssueType,
	type Nurse,
	type NurseFullRole,
	type NurseInShift,
	NurseRole,
	type ScheduleData,
	type Shift,
	ShiftType,
	SuggestionAction,
	type WeekData,
} from "./models";
import { isOverstaffed, isUnderstaffed, type RoleKey, rolesMap } from "./utilFunctions";

export const DONT_CHANGE_DATES: string[] = ["2024-11-28", "2024-11-29", "2024-12-24", "2024-12-25", "2024-12-31", "2025-01-01"];

type ShiftData = {
	dayIndex: number;
	shiftNo: number;
	nursesPool: Nurse[];
	differential: number;
};
type ShiftDataNoDiff = Omit<ShiftData, "differential">;

export function getAutoSchedulerShiftStaffingIssues(context: StaffingContext): StaffingIssue[] {
	const issues: StaffingIssue[] = [];
	if (context.weekIndex === 0) {
		return issues;
	}

	const allDayNursesInWeek = getAllNursesInWeek(context.currWeek, context.nurseMap, ShiftType.Day);
	const allNightNursesInWeek = getAllNursesInWeek(context.currWeek, context.nurseMap, ShiftType.Night);

	const backToBackShifts = getBackToBackShiftsThisWeek(context.currWeek, context.hospitalData, context.weekIndex);
	handleBackToBackShifts(
		issues,
		context.currWeek,
		context.hospitalData,
		context.weekIndex,
		backToBackShifts,
		context.calculateShiftTimes,
		context.lastNameFirst,
	);

	const understaffedShifts: Record<string, ShiftData> = {};
	const overstaffedShifts: Record<string, ShiftData> = {};

	const balancedDayShifts: Record<string, ShiftDataNoDiff> = {};
	const balancedNightShifts: Record<string, ShiftDataNoDiff> = {};

	setOverstaffedUnderstaffedData(
		context,
		understaffedShifts,
		overstaffedShifts,
		balancedDayShifts,
		balancedNightShifts,
		allDayNursesInWeek,
		allNightNursesInWeek,
	);

	// Sort overstaffed and understaffed shifts by differential (descending order)
	const sortedOverstaffedShifts = Object.entries(overstaffedShifts).sort((a, b) => b[1].differential - a[1].differential);
	const sortedUnderstaffedShifts = Object.entries(understaffedShifts).sort((a, b) => b[1].differential - a[1].differential);

	// Get the differential by shift type to understand the overall staffing balance
	const differentialByShiftType = getDifferentialByShiftType(sortedOverstaffedShifts, sortedUnderstaffedShifts, context.currWeek);

	// Iterate over sorted overstaffed shifts to balance them with understaffed shifts
	for (const [overKey, overValue] of sortedOverstaffedShifts) {
		const [fromShiftNo, fromDayNo] = overKey.split("_").map(Number);

		// If no more understaffed shifts of the same type, distribute extra shifts across other days
		if (differentialByShiftType[context.currWeek.weeklyShifts[fromShiftNo].type].understaffed === 0) {
			// Skip if only 1 extra nurse (can't be redistributed)
			if (overstaffedShifts[overKey].differential === 1) continue;

			// Handle the case where only overstaffed shifts exist
			handleOnlyOverstaffedShifts(context, issues, overstaffedShifts, balancedDayShifts, balancedNightShifts, fromShiftNo, fromDayNo, overKey);
		} else {
			// Try to balance overstaffed shifts with understaffed ones
			handleBalanceOverstaffedWithUnderstaffedShifts(
				issues,
				context.hospitalData,
				context.currWeek,
				context.weekIndex,
				fromShiftNo,
				fromDayNo,
				overKey,
				overValue,
				sortedUnderstaffedShifts,
			);
		}
	}

	// else if (sortedUnderstaffedShifts.length > 0) {
	//     handleOnlyUnderstaffedShifts(issues, currWeek, sortedUnderstaffedShifts);
	// }

	return issues;
}

function handleBackToBackShifts(
	issues: StaffingIssue[],
	currWeek: WeekData,
	hospitalData: ScheduleData,
	weekIndex: number,
	backToBackShifts: {
		[key: string]: { dayIndex: number; shiftNo: number; nurseIds: number[] };
	},
	calculateShiftTimes: HospitalDataContextType["calculateShiftTimes"],
	lastNameFirst: boolean,
) {
	for (const [key, backToBackShift] of Object.entries(backToBackShifts)) {
		const [shiftFromNo, dayFromNo] = key.split("_").map(Number);
		const shiftFrom = currWeek.weeklyShifts[shiftFromNo];

		// Get the nurse IDs that have back to back shifts
		const nurseIds = backToBackShift.nurseIds;
		// For each nurse with back to back shifts
		for (const nurseId of nurseIds) {
			const currentNurse = hospitalData.nurses.find((nurse) => nurse.id === nurseId);
			if (!currentNurse) continue;

			// verify nurse is still adjacement (it can be fixed dut to other moves done in previous iterations)
			if (!isNurseScheduledAdjacent(hospitalData, nurseId, dayFromNo, shiftFromNo, weekIndex)) continue;

			// Get all shifts in the week
			const weekShifts = currWeek.weeklyShifts;
			const suggestions: Suggestion[] = [];

			// Only consider the same shift type (day/night)
			const shiftToNo = shiftFromNo;

			const shiftTo = weekShifts[shiftToNo];
			// Only consider shifts of same type (day/night)
			if (shiftTo.type !== shiftFrom.type) continue;

			// Check each day in the shift
			for (let dayToNo = 0; dayToNo < shiftTo.days.length; dayToNo++) {
				// Skip if same day
				if (dayToNo === dayFromNo) continue;

				// Check if nurse is already assigned to this day
				const dayNurseIds = weekShifts[shiftToNo].days[dayToNo].nurses.map((nurse) => nurse.nurseId);
				if (dayNurseIds.includes(currentNurse.id)) continue;

				// Check if nurse can work this shift and it's not adjacement in this shift
				if (
					!isCannotWork(currentNurse, hospitalData, weekIndex, shiftToNo, dayToNo, calculateShiftTimes) &&
					!isNurseScheduledAdjacent(hospitalData, currentNurse.id, dayToNo, shiftToNo, weekIndex)
				) {
					// Check if nurse isn't already assigned to this shift
					if (!getNurseIdsForShift(hospitalData, dayToNo, shiftToNo, weekIndex).includes(currentNurse.id)) {
						suggestions.push({
							action: SuggestionAction.Move,
							nurseId: currentNurse.id,
							dayNo: dayFromNo,
							shiftNo: shiftFromNo,
							otherDayNo: dayToNo,
							otherShiftNo: shiftToNo,
							weekIndex: weekIndex,
							customDisplayText: currWeek.weeklyShifts[shiftToNo].days[dayToNo].date.toFormat("cccc"),
						});
					}
				}
			}
			// If we found suggestions for this nurse, add them to issues
			if (suggestions.length > 0) {
				// sort suggestions
				sortBackToBackOptions(hospitalData, currentNurse.allowedShifts, suggestions, currentNurse, weekIndex, dayFromNo);
				moveBackToBackNurseSuggestion(issues, suggestions, currWeek, shiftFromNo, dayFromNo, currentNurse, lastNameFirst);
			}
		}
	}
}

function sortBackToBackOptions(
	hospitalData: ScheduleData,
	consecutiveAllowedShifts: number,
	suggestions: Suggestion[],
	currentNurse: Nurse,
	weekIndex: number,
	ignoreDay: number,
) {
	suggestions.sort((a, b) => {
		// Get the target day/shift info for each suggestion
		const aDay: number | undefined = a.otherDayNo;
		const aShift: number | undefined = a.otherShiftNo;
		const bDay: number | undefined = b.otherDayNo;
		const bShift: number | undefined = b.otherShiftNo;

		// Check if either suggestion would create consecutive shifts
		const aExceedsConsecutive = checkMoreThanAllowedConsecutiveShifts(
			currentNurse,
			consecutiveAllowedShifts,
			weekIndex,
			aShift as number,
			aDay as number,
			hospitalData,
			ignoreDay,
		);
		const bExceedsConsecutive = checkMoreThanAllowedConsecutiveShifts(
			currentNurse,
			consecutiveAllowedShifts,
			weekIndex,
			bShift as number,
			bDay as number,
			hospitalData,
			ignoreDay,
		);

		if (aExceedsConsecutive !== bExceedsConsecutive) {
			return aExceedsConsecutive ? 1 : -1; // Prefer non-consecutive shifts
		}

		// Check if either day is a weekend day
		const aDate = hospitalData.weeks[weekIndex].weeklyShifts[aShift as number].days[aDay as number].date;
		const bDate = hospitalData.weeks[weekIndex].weeklyShifts[bShift as number].days[bDay as number].date;

		const aIsWeekend = isWeekend(aDate, hospitalData.weeks[weekIndex].weeklyShifts[aShift as number].type as ShiftType);
		const bIsWeekend = isWeekend(bDate, hospitalData.weeks[weekIndex].weeklyShifts[bShift as number].type as ShiftType);

		if (aIsWeekend !== bIsWeekend) {
			return aIsWeekend ? 1 : -1; // Prefer non-weekend days
		}

		// If all else equal, keep original order
		return 0;
	});
}

const getAllNursesInWeek = (currWeek: WeekData, nurseMap: Map<number, Nurse>, shiftType: ShiftType) => {
	const allNursesInWeek = new Set<Nurse>();
	currWeek.weeklyShifts.forEach((shift) => {
		if (shift.type === shiftType) {
			shift.days.forEach((day) => {
				const dayNurses = day.nurses
					.filter((nurse) => !nurse.isPendingToDelete && nurseMap.get(nurse.nurseId)?.role === rolesMap[NurseRole.RN])
					.map((nurse) => nurseMap.get(nurse.nurseId))
					.filter((nurse): nurse is Nurse => nurse !== undefined);
				dayNurses.forEach((nurse) => allNursesInWeek.add(nurse));
			});
		}
	});
	return allNursesInWeek;
};

function getBackToBackShiftsThisWeek(currWeek: WeekData, hospitalData: ScheduleData, weekIndex: number) {
	const backToBackShifts: {
		[key: string]: { dayIndex: number; shiftNo: number; nurseIds: number[] };
	} = {};
	for (let shiftNo = 0; shiftNo < currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts: Shift = currWeek.weeklyShifts[shiftNo];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const day: Day = weeklyShifts.days[dayIndex];
			const currentShiftNurses = day.nurses.filter((nurse: NurseInShift) => !nurse.isPendingToDelete);
			for (const nurse of currentShiftNurses) {
				if (isNurseScheduledAdjacent(hospitalData, nurse.nurseId, dayIndex, shiftNo, weekIndex)) {
					if (backToBackShifts[`${shiftNo}_${dayIndex}`]) {
						backToBackShifts[`${shiftNo}_${dayIndex}`].nurseIds.push(nurse.nurseId);
					} else {
						backToBackShifts[`${shiftNo}_${dayIndex}`] = {
							dayIndex,
							shiftNo,
							nurseIds: [nurse.nurseId],
						};
					}
				}
			}
		}
	}
	return backToBackShifts;
}

function setOverstaffedUnderstaffedData(
	context: StaffingContext,
	understaffedShifts: Record<string, ShiftData>,
	overstaffedShifts: Record<string, ShiftData>,
	balancedDayShifts: Record<string, ShiftDataNoDiff>,
	balancedNightShifts: Record<string, ShiftDataNoDiff>,
	allDayNursesInWeek: Set<Nurse>,
	allNightNursesInWeek: Set<Nurse>,
): void {
	let nursesToRemove: Nurse[] = [];
	let nursesToAdd: Nurse[] = [];
	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts = context.currWeek.weeklyShifts[shiftNo];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const nursesToCheck: Set<Nurse> = weeklyShifts.type === ShiftType.Day ? new Set(allDayNursesInWeek) : new Set(allNightNursesInWeek);
			const day = weeklyShifts.days[dayIndex];
			// ignore holidays
			if (DONT_CHANGE_DATES.includes(day.date.toFormat("yyyy-MM-dd"))) continue;
			const understaffed = isUnderstaffed(day);
			const overstaffed = isOverstaffed(day);
			const currentNursesNum = day.nurses.filter(
				(nurse: NurseInShift) => !nurse.isPendingToDelete && context.nurseMap.get(nurse.nurseId)?.role !== rolesMap[NurseRole.CNA],
			).length;
			// Get current nurses in shift as Set<Nurse>
			const currentNurses: Set<Nurse> = new Set(
				day.nurses
					.filter((nurse: NurseInShift) => !nurse.isPendingToDelete && context.nurseMap.get(nurse.nurseId)?.role !== rolesMap[NurseRole.CNA])
					.map((nurse: NurseInShift) => context.nurseMap.get(nurse.nurseId))
					.filter((nurse): nurse is Nurse => nurse !== undefined),
			);
			if (understaffed || overstaffed) {
				if (understaffed) {
					currentNurses.forEach((nurse) => {
						const fullNurse = context.nurseMap.get(nurse.id);
						if (fullNurse) nursesToCheck.delete(fullNurse);
					});

					// Filter out shift managers, per diem nurses, and nurses that can't work in this shift
					nursesToAdd = Array.from(nursesToCheck).filter((nurse) => {
						return (
							!nurse.isShiftManager &&
							!day.nurses.find((nurseToCheck) => nurseToCheck.nurseId === nurse.id)?.isPending &&
							nurse.employmentType !== EmploymentType.PerDiem &&
							(nurse.shiftType === ShiftType.All ||
								(weeklyShifts.type === ShiftType.Day && nurse.shiftType === ShiftType.Day) ||
								(weeklyShifts.type === ShiftType.Night && nurse.shiftType === ShiftType.Night)) &&
							nurse.role === rolesMap[NurseRole.RN] &&
							/*(() => {
                                    const changes = getNurseChanges(context.hospitalData, nurse.id, context.weekIndex);
                                    return Math.max(changes.additions.length, changes.removals.length) === 0;
                                })() &&*/
							!isCannotWork(nurse, context.hospitalData, context.weekIndex, shiftNo, dayIndex, context.calculateShiftTimes)
						);
					});
					nursesToAdd = sortNurses(
						context.hospitalData,
						shiftNo,
						context.weekIndex,
						dayIndex,
						context.nurseOcuurences,
						context.calculateShiftTimes,
						false,
						NurseRole.RN,
						true,
						nursesToAdd,
					);
					understaffedShifts[`${shiftNo}_${dayIndex}`] = {
						dayIndex,
						shiftNo,
						nursesPool: nursesToAdd,
						differential: Math.abs(currentNursesNum - day.staffNo),
					};
				} else {
					// Filter out shift managers, per diem nurses, and nurses that can work only on this shift
					nursesToRemove = Array.from(currentNurses).filter(
						(nurse) =>
							!nurse.isShiftManager &&
							!day.nurses.find((nurseToCheck) => nurseToCheck.nurseId === nurse.id)?.isPending &&
							nurse.employmentType !== EmploymentType.PerDiem &&
							nurse.role === rolesMap[NurseRole.RN],
						/*(() => {
                            const changes = getNurseChanges(context.hospitalData, nurse.id, context.weekIndex);
                            return Math.max(changes.additions.length, changes.removals.length) === 0;
                        })() &&*/
					);

					// Get all shift managers and relief managers
					const shiftManagers = Array.from(nursesToRemove).filter((nurse) => nurse.isShiftManager);
					const reliefManagers = Array.from(nursesToRemove).filter((nurse) => nurse.canBeReliefCharge);

					// in case no shift manager in this shift and there is only one relief manager - can't move to another shift
					if (shiftManagers.length === 0) {
						if (reliefManagers.length === 1) {
							nursesToRemove = nursesToRemove.filter((nurse) => nurse.id !== reliefManagers[0].id);
						}
					}
					overstaffedShifts[`${shiftNo}_${dayIndex}`] = {
						dayIndex,
						shiftNo,
						nursesPool: nursesToRemove,
						differential: Math.abs(currentNursesNum - day.staffNo),
					};
				}
			} else {
				if (!isWeekend(day.date, weeklyShifts.type as ShiftType)) {
					if (weeklyShifts.type === ShiftType.Day) {
						balancedDayShifts[`${shiftNo}_${dayIndex}`] = {
							dayIndex,
							shiftNo,
							nursesPool: Array.from(currentNurses),
						};
					} else {
						balancedNightShifts[`${shiftNo}_${dayIndex}`] = {
							dayIndex,
							shiftNo,
							nursesPool: Array.from(currentNurses),
						};
					}
				}
			}
		}
	}
}

// function handleOnlyUnderstaffedShifts(issues: StaffingIssue[], currWeek: WeekData, sortedUnderstaffedShifts: any){

// }

function handleBalanceOverstaffedWithUnderstaffedShifts(
	issues: StaffingIssue[],
	hospitalData: ScheduleData,
	currWeek: WeekData,
	weekIndex: number,
	fromShiftNo: number,
	fromDayNo: number,
	overKey: string,
	overValue: ShiftData,
	sortedUnderstaffed: [string, ShiftData][],
): void {
	for (const [underKey, _] of sortedUnderstaffed) {
		const suggestions: Suggestion[] = [];
		const bestMatches = findBestMatch(hospitalData, underKey, overKey, sortedUnderstaffed, currWeek, weekIndex, overValue);
		if (bestMatches.length === 0) continue;

		const [toShiftNo, toDayNo] = underKey.split("_").map(Number);
		if (typeof toShiftNo !== "number" || typeof toDayNo !== "number") continue;

		for (const bestMatch of bestMatches) {
			const newSuggestion: Suggestion = {
				action: SuggestionAction.Move,
				nurseId: bestMatch.id,
				dayNo: fromDayNo,
				shiftNo: fromShiftNo,
				otherDayNo: toDayNo,
				otherShiftNo: toShiftNo,
				weekIndex,
			};

			const existingIssue = issues.find((issue) => {
				return issue.suggestions.some((suggestion) => {
					return (
						suggestion.action === newSuggestion.action &&
						suggestion.otherShiftNo === newSuggestion.otherShiftNo &&
						suggestion.otherDayNo === newSuggestion.otherDayNo
					);
				});
			});

			if (existingIssue) {
				existingIssue.suggestions.push(newSuggestion);
			} else {
				suggestions.push(newSuggestion);
			}
		}

		if (suggestions.length > 0) {
			issues.push({
				issueType: IssueType.Shift,
				priority: IssuePriority.TopPriority,
				title: [{ type: "bold", text: "understaffed shift" }],
				suggestions,
			});
		}
	}
}

function getDifferentialByShiftType(sortedOverstaffed: any, sortedUnderstaffed: any, currWeek: WeekData) {
	// calculate differential by shift type
	const differentialByShiftType: {
		[key: string]: { overstaffed: number; understaffed: number };
	} = {
		[ShiftType.Day]: { overstaffed: 0, understaffed: 0 },
		[ShiftType.Night]: { overstaffed: 0, understaffed: 0 },
	};

	for (const [_, value] of sortedOverstaffed) {
		const shiftType = currWeek.weeklyShifts[(value as { shiftNo: number }).shiftNo].type;
		differentialByShiftType[shiftType].overstaffed += (value as { differential: number }).differential;
	}

	for (const [_, value] of sortedUnderstaffed) {
		const shiftType = currWeek.weeklyShifts[(value as { shiftNo: number }).shiftNo].type;
		differentialByShiftType[shiftType].understaffed += (value as { differential: number }).differential;
	}

	return differentialByShiftType;
}

function moveBackToBackNurseSuggestion(
	issues: StaffingIssue[],
	suggestions: Suggestion[],
	currWeek: WeekData,
	fromShiftNo: number,
	fromDayNo: number,
	currentNurse: Nurse,
	lastNameFirst: boolean,
) {
	issues.push({
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		title: [
			{ type: "normal", text: "Staff member" },
			{
				type: "bold",
				text: `${formatPersonFullName(currentNurse, lastNameFirst)} `,
			},
			{ type: "normal", text: "has " },
			{ type: "bold", text: "back to back shifts, " },
			{ type: "normal", text: "consider moving from " },
			{
				type: "bold",
				text: ` ${currWeek.weeklyShifts[fromShiftNo].days[fromDayNo].date.toFormat("cccc")}`,
			},
			{ type: "bold", text: ` ${currWeek.weeklyShifts[fromShiftNo].type}` },
		],
		suggestions: suggestions.slice(0, 3),
	});
}

function moveNurseSuggestion(
	issues: StaffingIssue[],
	suggestions: Suggestion[],
	currWeek: WeekData,
	fromShiftNo: number,
	fromDayNo: number,
	toShiftNo: number,
	toDayNo: number,
): void {
	issues.push({
		issueType: IssueType.Shift,
		priority: IssuePriority.TopPriority,
		title: [
			{ type: "normal", text: "Consider moving staff member" },
			{
				type: "bold",
				text: ` ${currWeek.weeklyShifts[fromShiftNo].days[fromDayNo].date.toFormat("cccc")}`,
			},
			{ type: "bold", text: ` ${currWeek.weeklyShifts[fromShiftNo].type}` },
			{ type: "normal", text: " to" },
			{
				type: "bold",
				text: ` ${currWeek.weeklyShifts[toShiftNo].days[toDayNo].date.toFormat("cccc")}`,
			},
			{ type: "bold", text: ` ${currWeek.weeklyShifts[toShiftNo].type}` },
		],
		suggestions,
	});
}

function handleOnlyOverstaffedShifts(
	context: StaffingContext,
	issues: StaffingIssue[],
	overstaffedShifts: Record<string, ShiftData>,
	balancedDayShifts: Record<string, ShiftDataNoDiff>,
	balancedNightShifts: Record<string, ShiftDataNoDiff>,
	fromShiftNo: number,
	fromDayNo: number,
	overKey: string,
) {
	const shiftType = context.currWeek.weeklyShifts[fromShiftNo].type;
	const balancedShifts = shiftType === ShiftType.Day ? balancedDayShifts : balancedNightShifts;
	for (const [balancedKey, _] of Object.entries(balancedShifts)) {
		const nursesToRemove: Nurse[] = overstaffedShifts[overKey].nursesPool;
		const suggestions: Suggestion[] = [];
		const [balancedShiftNo, balancedDayNo] = balancedKey.split("_").map(Number);
		if (balancedShiftNo !== fromShiftNo) continue;

		// sort nurses acording to the date to move nurses to
		const nursesToAdd = sortNurses(
			context.hospitalData,
			balancedShiftNo,
			context.weekIndex,
			balancedDayNo,
			context.nurseOcuurences,
			context.calculateShiftTimes,
			false,
			NurseRole.RN,
			true,
			nursesToRemove,
		);
		// Check if the balanced shift is suitable for the nurse to be removed
		for (let i = 0; i < nursesToAdd.length && suggestions.length < 3; i++) {
			const nurse = nursesToAdd[i];
			if (getNurseIdsForShift(context.hospitalData, balancedDayNo, balancedShiftNo, context.weekIndex).includes(nurse.id)) {
				continue;
			}
			if (!isCannotWork(nurse, context.hospitalData, context.weekIndex, balancedShiftNo, balancedDayNo, context.calculateShiftTimes)) {
				suggestions.push({
					action: SuggestionAction.Move,
					nurseId: nurse.id,
					dayNo: fromDayNo,
					shiftNo: fromShiftNo,
					otherDayNo: balancedDayNo,
					otherShiftNo: balancedShiftNo,
					weekIndex: context.weekIndex,
				});
			}
		}
		if (suggestions.length > 0) {
			moveNurseSuggestion(issues, suggestions, context.currWeek, fromShiftNo, fromDayNo, balancedShiftNo, balancedDayNo);
		}
	}
}

function findBestMatch(
	hospitalData: ScheduleData,
	underKey: string,
	overKey: string,
	sortedUnderstaffed: [string, ShiftData][],
	currWeek: WeekData,
	weekIndex: number,
	overValue: ShiftData,
): Nurse[] {
	const [overShiftNo, _overDayNo] = overKey.split("_").map(Number);
	const [underShiftNo, _underDayNo] = underKey.split("_").map(Number);

	const overShiftType = currWeek.weeklyShifts[overShiftNo].type;
	const underShiftType = currWeek.weeklyShifts[underShiftNo].type;

	if (overShiftType !== underShiftType) {
		return [];
	}

	const underKeyIndex = sortedUnderstaffed.findIndex(([key, _]) => key === underKey);
	if (underKeyIndex < 0) {
		return [];
	}

	const sortedNursesToAdd = sortedUnderstaffed[underKeyIndex][1].nursesPool;
	const overNursesToRemove = overValue.nursesPool;

	// // SORTING - NEW CODE FOR IMPROVED MATCHING - NOT WORKING
	// // sort nurses by which one is the most fit to remove from the overstaffed shift
	// const sortedOverNursesToRemove = sortNurses(hospitalData, overShiftNo, weekIndex, overDayNo, true, NurseRole.RN, true, overNursesToRemove);

	// // Create a map to store the sum of indexes for each nurse
	// const nurseIndexSum = new Map<number, { nurse: Nurse, sum: number }>();

	// // Calculate the sum of indexes for each nurse
	// sortedOverNursesToRemove.forEach((nurse, index) => {
	//     const addIndex = sortedNursesToAdd.findIndex((n: Nurse) => n.id === nurse.id);
	//     if (addIndex !== -1) {
	//         nurseIndexSum.set(nurse.id, { nurse, sum: index + addIndex });
	//     }
	// });

	// // Convert the map to an array and sort it by the sum of indexes
	// const matchedNurses = Array.from(nurseIndexSum.values())
	//     .sort((a, b) => a.sum - b.sum)
	//     .map(({ nurse }) => ({
	//         id: nurse.id,
	//         firstName: nurse.firstName,
	//         lastName: nurse.lastName
	//     }));

	// return matchedNurses;

	// OLD CODE - WORKS

	return sortedNursesToAdd.filter((nurseToAdd) => {
		return overNursesToRemove.some((nurseToRemove) => nurseToRemove.id === nurseToAdd.id);
	});
}

function checkLpnLimit(
	nurseIssues: StaffingIssue[],
	day: Day,
	shiftNo: number,
	dayIndex: number,
	weekIndex: number,
	weeklyShifts: Shift,
	nurseMap: Map<number, Nurse>,
) {
	// Initialize LPN counter for each shift
	let lpnCount = 0;
	let suggestions: Suggestion[] = [];
	// Count LPNs in the current shift
	for (const nurse of day.nurses) {
		if (!nurse.isPendingToDelete) {
			const nurseData = nurseMap.get(nurse.nurseId);
			if (nurseData && nurseData.role === rolesMap[NurseRole.LPN]) {
				lpnCount++;
			}
			suggestions = [
				{
					action: SuggestionAction.Remove,
					nurseId: nurse.nurseId,
					dayNo: dayIndex,
					shiftNo: shiftNo,
					weekIndex: weekIndex,
				},
				...suggestions,
			];
		}
	}
	// If there's more than one LPN, create an issue
	if (lpnCount > 1) {
		const lpnIssue: StaffingIssue = {
			issueType: IssueType.Shift,
			priority: IssuePriority.Important,
			title: [
				{ type: "bold", text: "Too many LPNs" },
				{ type: "normal", text: " in " },
				{ type: "bold", text: `${weeklyShifts.type}` },
				{ type: "normal", text: " shift on " },
				{ type: "bold", text: `${day.date.toFormat("cccc")}.` },
				{
					type: "normal",
					text: " Consider removing one so there will be only 1 LPN.",
				},
			],
			suggestions: suggestions,
		};
		nurseIssues.push(lpnIssue);
	}
}

function checkNurseDeletion(
	nurseIssues: StaffingIssue[],
	day: Day,
	weeklyShifts: Shift,
	nurseMap: Map<number, Nurse>,
	checkCondition: (nurseData: Nurse) => boolean,
	nurseDescription: string,
	shiftNo: number,
	dayIndex: number,
	weekIndex: number,
	lastNameFirst: boolean,
) {
	for (const nurse of day.nurses) {
		if (nurse.isPendingToDelete) {
			const nurseData = nurseMap.get(nurse.nurseId);
			if (nurseData && checkCondition(nurseData)) {
				if (nurseData.shiftType !== weeklyShifts.type) continue;
				const issue: StaffingIssue = {
					issueType: IssueType.Nurse,
					priority: IssuePriority.TopPriority,
					title: [
						{
							type: "bold",
							text: formatPersonFullName(nurseData, lastNameFirst),
						},
						{ type: "normal", text: " is a " },
						{ type: "bold", text: ` ${nurseDescription} ` },
						{ type: "normal", text: " and is " },
						{ type: "bold", text: "pending deletion" },
						{ type: "normal", text: " from " },
						{ type: "bold", text: ` ${weeklyShifts.type} ` },
						{ type: "normal", text: " shift on " },
						{ type: "bold", text: `${day.date.toFormat("cccc")}.` },
						{ type: "normal", text: "Please review this change carefully." },
					],
					suggestions: [
						{
							action: SuggestionAction.Replace,
							nurseId: nurse.nurseId,
							dayNo: dayIndex,
							shiftNo: shiftNo,
							otherNurseId: nurse.nurseId,
							weekIndex: weekIndex,
						},
					],
				};
				nurseIssues.push(issue);
			}
		}
	}
}

function checkShiftManagerDeletion(
	nurseIssues: StaffingIssue[],
	day: Day,
	weeklyShifts: Shift,
	nurseMap: Map<number, Nurse>,
	shiftNo: number,
	dayIndex: number,
	weekIndex: number,
	lastNameFirst: boolean,
) {
	checkNurseDeletion(
		nurseIssues,
		day,
		weeklyShifts,
		nurseMap,
		(nurseData) => nurseData.isShiftManager,
		"shift manager",
		shiftNo,
		dayIndex,
		weekIndex,
		lastNameFirst,
	);
}

function checkPRNDeletion(
	nurseIssues: StaffingIssue[],
	day: Day,
	weeklyShifts: Shift,
	nurseMap: Map<number, Nurse>,
	shiftNo: number,
	dayIndex: number,
	weekIndex: number,
	lastNameFirst: boolean,
) {
	checkNurseDeletion(
		nurseIssues,
		day,
		weeklyShifts,
		nurseMap,
		(nurseData) => nurseData.employmentType === EmploymentType.PerDiem,
		"PRN RN",
		shiftNo,
		dayIndex,
		weekIndex,
		lastNameFirst,
	);
}

function checkChargeNurseRequirement(
	nurseIssues: StaffingIssue[],
	day: Day,
	weeklyShifts: Shift,
	nurseMap: Map<number, Nurse>,
	hospitalData: ScheduleData,
	weekIndex: number,
	shiftNo: number,
	dayIndex: number,
) {
	const hasChargeNurse = day.nurses.some((nurse) => {
		const nurseData = nurseMap.get(nurse.nurseId);
		return (nurseData && !nurse.isPendingToDelete) || nurseData?.isShiftManager || nurseData?.canBeReliefCharge;
	});

	if (!hasChargeNurse) {
		const shiftType = shiftNo === 0 ? ShiftType.Day : ShiftType.Night;
		const overstaffedShift = hospitalData.weeks[weekIndex].weeklyShifts
			.flatMap((shift, sIndex) => shift.days.map((d, dIndex) => ({ shift, sIndex, d, dIndex })))
			.find(
				({ shift, d }) =>
					shift.type === weeklyShifts.type &&
					isOverstaffed(d) &&
					d.nurses.some((n) => {
						const nurseData = nurseMap.get(n.nurseId);
						return nurseData?.canBeReliefCharge;
					}),
			);

		if (overstaffedShift) {
			const { sIndex: fromShiftNo, dIndex: fromDayNo, d: fromDay } = overstaffedShift;
			const nurseToMove = fromDay.nurses.find((n) => {
				const nurseData = nurseMap.get(n.nurseId);
				return (
					nurseData &&
					nurseData.role === rolesMap[NurseRole.RN] &&
					nurseData.canBeReliefCharge &&
					nurseData.employmentType !== EmploymentType.PerDiem &&
					!nurseData.isShiftManager
				);
			});

			if (nurseToMove) {
				nurseIssues.push({
					issueType: IssueType.Shift,
					priority: IssuePriority.TopPriority,
					title: [
						{ type: "bold", text: "No Charge Nurse or relief nurse" },
						{ type: "normal", text: " in " },
						{ type: "bold", text: ` ${weeklyShifts.type} ` },
						{ type: "normal", text: " shift on " },
						{ type: "bold", text: `${day.date.toFormat("cccc")}.` },
					],
					suggestions: [
						{
							action: SuggestionAction.Move,
							nurseId: nurseToMove.nurseId,
							shiftNo: fromShiftNo,
							dayNo: fromDayNo,
							otherShiftNo: shiftNo,
							otherDayNo: dayIndex,
							weekIndex: weekIndex,
						},
					],
				});
			}
		} else {
			const issue: StaffingIssue = {
				issueType: IssueType.Shift,
				priority: IssuePriority.TopPriority,
				title: [
					{ type: "bold", text: "No Charge Nurse or relief nurse" },
					{ type: "normal", text: " in " },
					{ type: "bold", text: ` ${weeklyShifts.type} ` },
					{ type: "normal", text: " shift on " },
					{ type: "bold", text: `${day.date.toFormat("cccc")}.` },
				],
				suggestions: [
					...hospitalData.nurses
						.filter((nurse) => {
							const nurseData = nurseMap.get(nurse.id);
							return nurseData?.canBeReliefCharge && nurse.shiftType === shiftType && nurseData.employmentType !== EmploymentType.PerDiem;
						})
						.map((nurse) => ({
							action: SuggestionAction.Add,
							nurseId: nurse.id,
							shiftNo: shiftNo,
							dayNo: dayIndex,
							weekIndex: weekIndex,
						}))
						.sort((a, b) => {
							const countShifts = (nurseId: number) => {
								let count = 0;
								for (const shift of hospitalData.weeks[weekIndex].weeklyShifts) {
									for (const day of shift.days) {
										if (day.nurses.some((n) => n.nurseId === nurseId && !n.isPendingToDelete)) {
											count++;
										}
									}
								}
								return count;
							};

							const countA = countShifts(a.nurseId);
							const countB = countShifts(b.nurseId);

							// Ignore nurses with 0 shifts
							if (countA === 0 && countB === 0) return 0;
							if (countA === 0) return 1;
							if (countB === 0) return -1;

							// If shift counts are equal, prioritize originally assigned nurse
							if (countA === countB) {
								const nurseAOriginallyAssigned = day.nurses.some((n) => n.nurseId === a.nurseId);
								const nurseBOriginallyAssigned = day.nurses.some((n) => n.nurseId === b.nurseId);
								if (nurseAOriginallyAssigned && !nurseBOriginallyAssigned) return -1;
								if (!nurseAOriginallyAssigned && nurseBOriginallyAssigned) return 1;
							}

							return countA - countB; // Ascending order (fewer shifts first)
						})
						.slice(0, 3),
				],
			};
			nurseIssues.push(issue);
		}
	}
}

export function getNurseAutoSchedulerStaffingIssues(
	context: StaffingContext,
	role: RoleKey,
	departmentNurseTypes: NurseFullRole[],
	timezone: string,
	isLowry: boolean,
) {
	// will present issues regarding the nurses:
	// 1. LPN Limit: Ensure only 1 LPN per shift
	// 2. Permanent Charge RN Shifts: Verify that Permanent Charge RN shifts are not changed
	// 3. PRN RN Shifts: Ensure PRN RN shifts are not changed
	// 4. Charge Nurse Requirement: Check if every shift has a permanent charge or relief charge nurse (relief charge is a nurse that can be relief manager and wants to be relief manager)
	// 5. Weekend Requirements: Check if weekend staffing requirements are met
	// 6. FTE/PT/PRN/Flex Requirements: Verify that each nurse type meets their specific scheduling requirements
	// 7. Nurse is working more than the required
	// 8. day nurse added to night shift and vice versa
	if (context.weekIndex === 0) return [];
	const nurseIssues: StaffingIssue[] = [];
	const nurseCount: Map<number, Array<ShiftDetails>> = new Map();

	for (let shiftNo = 0; shiftNo < context.currWeek.weeklyShifts.length; shiftNo++) {
		const weeklyShifts: Shift = context.currWeek.weeklyShifts[shiftNo];
		for (let dayIndex = 0; dayIndex < weeklyShifts.days.length; dayIndex++) {
			const day: Day = weeklyShifts.days[dayIndex];
			// Ensure only 1 LPN per shift
			checkLpnLimit(nurseIssues, day, shiftNo, dayIndex, context.weekIndex, weeklyShifts, context.nurseMap);
			// Check if any nurse is a shift manager and pending deletion
			checkShiftManagerDeletion(nurseIssues, day, weeklyShifts, context.nurseMap, shiftNo, dayIndex, context.weekIndex, context.lastNameFirst);
			// Check if any PRN RN Shifts are not changed
			checkPRNDeletion(nurseIssues, day, weeklyShifts, context.nurseMap, shiftNo, dayIndex, context.weekIndex, context.lastNameFirst);
			// Check if every shift has a permanent charge or relief charge nurse
			checkChargeNurseRequirement(nurseIssues, day, weeklyShifts, context.nurseMap, context.hospitalData, context.weekIndex, shiftNo, dayIndex);

			for (const nurse of day.nurses) {
				if (nurse.isPendingToDelete || nurse.isPending) continue;
				updateNurseCount(nurseCount, nurse.nurseId, context.weekIndex, shiftNo, dayIndex, day.date, nurse.startTime, nurse.endTime);
				// 8. day nurse added to night shift and vice versa
				// const nurse_data: Nurse | undefined = nurseMap.get(nurse.id)
				// checkNurseAddedToWrongShiftType(nurseIssues, nurse_data, day.date, weeklyShifts, nurseMap, shiftNo, dayIndex, weekIndex);
			}
		}
	}

	// checking if nurse working more than the required
	// overtime nurse
	handleOverTimeNurse(context, nurseCount, nurseIssues, departmentNurseTypes, timezone, isLowry, context.calculateShiftTimes, true);

	return nurseIssues;
}
